import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import BlinterestSettings from "@/components/BlinterestSettings";
import BlinterestManagement from "@/components/BlinterestManagement";

/**
 * Admin: Blinterest moderation queue + ingester controls.
 * This is now a thin orchestrator — every UI section lives in its own
 * component (BlinterestSettings / BlinterestManagement / BlinterestPinForm).
 */
export default function AdminBlinterestTab() {
  const [status, setStatus] = useState({ running: false });
  const [statusFilter, setStatusFilter] = useState("draft");
  const [sourceFilter, setSourceFilter] = useState("");
  const [pins, setPins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ingestLimit, setIngestLimit] = useState(60);
  const [crawlLimit, setCrawlLimit] = useState(20);
  const [crawlPerBiz, setCrawlPerBiz] = useState(8);
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsFilter, setReportsFilter] = useState("pending");

  const fetchReports = useCallback(async () => {
    setReportsLoading(true);
    try {
      const r = await axios.get(
        `${API}/blinterest/admin/reports?status=${encodeURIComponent(reportsFilter)}`,
        { withCredentials: true },
      );
      setReports(r.data.reports || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't load reports");
    } finally {
      setReportsLoading(false);
    }
  }, [reportsFilter]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const dismissReport = async (report_id) => {
    try {
      await axios.post(`${API}/blinterest/admin/reports/${report_id}/dismiss`, {}, { withCredentials: true });
      setReports((prev) => prev.filter((r) => r.report_id !== report_id));
      toast.success("Dismissed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't dismiss");
    }
  };

  const resolveReport = async (report_id, removePin = false) => {
    try {
      await axios.post(
        `${API}/blinterest/admin/reports/${report_id}/resolve?remove_pin=${removePin}`,
        {}, { withCredentials: true },
      );
      setReports((prev) => prev.filter((r) => r.report_id !== report_id));
      toast.success(removePin ? "Pin removed" : "Resolved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't resolve");
    }
  };

  const fetchStatus = async () => {
    try {
      const r = await axios.get(`${API}/blinterest/admin/ingest/status`, { withCredentials: true });
      setStatus(r.data);
    } catch {
      /* silent */
    }
  };

  const fetchPins = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      if (sourceFilter) params.set("submission_source", sourceFilter);
      params.set("limit", "80");
      const r = await axios.get(`${API}/blinterest/admin/pins?${params.toString()}`, { withCredentials: true });
      setPins(r.data.pins || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't load pins");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, sourceFilter]);

  useEffect(() => { fetchPins(); fetchStatus(); }, [fetchPins]);

  useEffect(() => {
    if (!status.running) return;
    const id = setInterval(fetchStatus, 3000);
    return () => clearInterval(id);
  }, [status.running]);

  const kickIngest = async () => {
    try {
      const r = await axios.post(
        `${API}/blinterest/admin/ingest?limit=${ingestLimit}&verified_only=false&only_missing=true`,
        {}, { withCredentials: true },
      );
      if (r.data.ok) {
        toast.success(`Homepage ingest started (${ingestLimit} businesses)`);
        fetchStatus();
      } else {
        toast.error(r.data.error || "Couldn't start");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't start ingest");
    }
  };

  const kickDeepCrawl = async () => {
    try {
      const params = new URLSearchParams({
        limit_businesses: String(crawlLimit),
        pins_per_business: String(crawlPerBiz),
        verified_only: "false",
      });
      const r = await axios.post(
        `${API}/blinterest/admin/ingest/deep-crawl?${params.toString()}`,
        {}, { withCredentials: true },
      );
      if (r.data.ok) {
        toast.success(`Deep crawl started (${crawlLimit} businesses × ${crawlPerBiz} pins)`);
        fetchStatus();
      } else {
        toast.error(r.data.error || "Couldn't start");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't start deep crawl");
    }
  };

  const publish = async (pin_id) => {
    try {
      await axios.post(`${API}/blinterest/admin/pins/${pin_id}/publish`, {}, { withCredentials: true });
      setPins((prev) => prev.filter((p) => p.pin_id !== pin_id));
      toast.success("Published");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't publish");
    }
  };

  const remove = async (pin_id) => {
    try {
      await axios.post(`${API}/blinterest/admin/pins/${pin_id}/remove`, {}, { withCredentials: true });
      setPins((prev) => prev.filter((p) => p.pin_id !== pin_id));
      toast.success("Removed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't remove");
    }
  };

  const updateCta = async (pin_id, cta_type) => {
    try {
      await axios.patch(
        `${API}/blinterest/pins/${pin_id}`,
        { cta_type: cta_type || "" },
        { withCredentials: true },
      );
      setPins((prev) => prev.map((p) => (p.pin_id === pin_id ? { ...p, cta_type } : p)));
      toast.success("CTA updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't update CTA");
    }
  };

  const bulkPublish = async () => {
    if (!window.confirm(`Publish all ${pins.length} pins currently in the queue?`)) return;
    try {
      const r = await axios.post(`${API}/blinterest/admin/pins/bulk-publish`, {}, { withCredentials: true });
      toast.success(`Published ${r.data.published} pins`);
      fetchPins();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't bulk publish");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-blinterest-tab">
      <BlinterestSettings
        status={status}
        ingestLimit={ingestLimit}
        onIngestLimitChange={setIngestLimit}
        crawlLimit={crawlLimit}
        onCrawlLimitChange={setCrawlLimit}
        crawlPerBiz={crawlPerBiz}
        onCrawlPerBizChange={setCrawlPerBiz}
        onKickIngest={kickIngest}
        onKickDeepCrawl={kickDeepCrawl}
        onRefreshStatus={fetchStatus}
      />
      <BlinterestManagement
        pins={pins}
        loading={loading}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        sourceFilter={sourceFilter}
        onSourceFilterChange={setSourceFilter}
        onBulkPublish={bulkPublish}
        onPublish={publish}
        onRemove={remove}
        onUpdateCta={updateCta}
        reports={reports}
        reportsLoading={reportsLoading}
        reportsFilter={reportsFilter}
        onReportsFilterChange={setReportsFilter}
        onDismissReport={dismissReport}
        onResolveReport={resolveReport}
      />
    </div>
  );
}
